/**
 * Vuex actions for the prism module.
 *
 * @module store/modules/prism/actions
 */
import { graph } from "@/store/api";
import { RootState } from "@/store/types";
import { ActionTree } from "vuex/types";
import { PrismState } from "./types";

import { gql } from "@apollo/client/core";
import _ from "lodash";

const actions: ActionTree<PrismState, RootState> = {
  /**
   * Generates embeddings for an array of sentences using Prism AI.
   *
   * @param {ActionContext} store - Vuex action context
   * @param {Object} payload - Action payload
   * @param {string[]} payload.sentences - Array of sentences to embed
   * @returns {Promise<Array>} Array of embedding vectors
   * @throws {Error} If embedding generation fails
   *
   * @example
   * const embeddings = await this.$store.dispatch('EMBED_SENTENCES', {
   *   sentences: ['Hello world', 'How are you', 'Good morning']
   * });
   */
  EMBED_SENTENCES: (store, { sentences }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation($sentences: [String]) {
            prismAPI {
              getEmbeddings(sentences: $sentences)
            }
          }
        `,
        variables: { sentences },
      })
      .then((response) => {
        const data = _.get(response, "data.prismAPI.getEmbeddings");
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Runs t-SNE dimensionality reduction on embeddings for visualization.
   *
   * @param {ActionContext} store - Vuex action context
   * @param {Object} payload - Action payload
   * @param {Array} payload.embeddings - High-dimensional embedding vectors
   * @param {number} payload.perplexity - t-SNE perplexity parameter (typical: 5-50)
   * @returns {Promise<Array>} 2D/3D coordinates for visualization
   * @throws {Error} If t-SNE computation fails
   *
   * @example
   * const tsneOutput = await this.$store.dispatch('RUN_TSNE', {
   *   embeddings: embeddingVectors,
   *   perplexity: 30
   * });
   */
  RUN_TSNE: (store, { embeddings, perplexity }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation($embeddings: JSON, $perplexity: Int) {
            prismAPI {
              getTSNEOutput(embeddings: $embeddings, perplexity: $perplexity)
            }
          }
        `,
        variables: { embeddings, perplexity },
      })
      .then((response) => {
        const data = _.get(response, "data.prismAPI.getTSNEOutput");
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
};

export default actions;
